import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout, { StatCard, EmptyState } from "./DashboardLayout";
import { LayoutDashboard, BookOpen, FolderKanban, Award, IdCard, Trophy, ArrowRight, CheckCircle2, Clock, Bot, ClipboardList } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const NAV = [
    { key: "overview", label: "Dashboard", icon: LayoutDashboard },
    { key: "learning", label: "My Learning", icon: BookOpen },
    { key: "projects", label: "Projects", icon: FolderKanban },
    { key: "assessments", label: "Assessments", icon: ClipboardList },
    { key: "passport", label: "Digital Passport", icon: IdCard },
    { key: "credentials", label: "Credentials", icon: Award },
];

const STATUS_COLOR = { Approved: "success", "Under Review": "accent", Feedback: "accent", Revision: "destructive", Assigned: "muted", Submitted: "primary", Started: "primary" };

export default function StudentDashboard() {
    const [tab, setTab] = useState("overview");
    const [data, setData] = useState(null);
    const [assessments, setAssessments] = useState([]);
    const navigate = useNavigate();
    const [explainFor, setExplainFor] = useState(null);
    const [ex, setEx] = useState({ what: "", why: "", tools: "", decisions: "", problems: "", ai_tools: "", learned: "", video_url: "" });
    const setField = (k) => (e) => setEx((s) => ({ ...s, [k]: e.target.value }));
    const submitExplain = async () => {
        try {
            await api.post(`/projects/${explainFor.id}/explain`, ex);
            toast.success("Submitted with your explanation for mentor review!");
            setExplainFor(null);
            load();
        } catch { toast.error("Could not submit. Please try again."); }
    };

    const load = () => api.get("/me/dashboard").then((r) => setData(r.data)).catch(() => setData({}));
    useEffect(() => {
        load();
        api.get("/assessments").then((r) => setAssessments(r.data)).catch(() => {});
    }, []);

    const submit = async (id) => {
        try { await api.post(`/projects/${id}/submit`); toast.success("Project submitted for review!"); load(); }
        catch { toast.error("Could not submit. Please try again."); }
    };

    if (!data) return <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Student Dashboard"><div className="grid place-items-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div></DashboardLayout>;

    const enr = data.enrollments?.[0];
    const projects = data.projects || [];
    const badges = data.badges || [];
    const certs = data.certificates || [];

    return (
        <DashboardLayout nav={NAV} active={tab} onNav={setTab} title="Student Dashboard">
            {tab === "overview" && (
                <div className="space-y-6">
                    <div className="rounded-2xl bg-secondary text-secondary-foreground p-6 sm:p-8">
                        <p className="text-secondary-foreground/60 text-sm">Welcome back,</p>
                        <h2 className="font-heading text-2xl font-bold">{data.user?.name} 👋</h2>
                        {enr ? (
                            <div className="mt-6 rounded-xl bg-white/5 border border-white/10 p-5">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                    <div>
                                        <p className="text-xs text-secondary-foreground/50 uppercase tracking-wide">What's next</p>
                                        <p className="mt-1 font-heading font-semibold">{enr.next_action}</p>
                                        <p className="text-sm text-secondary-foreground/60 mt-1">in {enr.program_title} · {enr.current_lesson}</p>
                                    </div>
                                    <Button className="rounded-full" data-testid="continue-learning-btn" onClick={() => navigate(`/learn/${enr.program_slug}`)}>Continue <ArrowRight className="ml-1 h-4 w-4" /></Button>
                                </div>
                                <div className="mt-4"><div className="flex justify-between text-xs text-secondary-foreground/60 mb-1"><span>Progress</span><span>{enr.progress}%</span></div><Progress value={enr.progress} className="h-2" /></div>
                            </div>
                        ) : <p className="mt-4 text-secondary-foreground/60">You're not enrolled in a program yet.</p>}
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard icon={BookOpen} label="Active Program" value={enr ? 1 : 0} />
                        <StatCard icon={FolderKanban} label="Projects" value={projects.length} color="accent" />
                        <StatCard icon={Trophy} label="Badges" value={badges.length} color="success" />
                        <StatCard icon={Award} label="Credentials" value={certs.length} />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        <Card className="p-6">
                            <h3 className="font-heading font-bold flex items-center gap-2"><Trophy className="h-5 w-5 text-accent" /> Badges earned</h3>
                            <div className="mt-4 flex flex-wrap gap-2">{badges.length ? badges.map((b) => <Badge key={b.id} variant="secondary" className="rounded-full">{b.name}</Badge>) : <p className="text-sm text-foreground/50">No badges yet.</p>}</div>
                        </Card>
                        <Card className="p-6">
                            <h3 className="font-heading font-bold flex items-center gap-2"><Bot className="h-5 w-5 text-primary" /> AI Mentor</h3>
                            <p className="mt-2 text-sm text-foreground/60">Stuck on something? Your AI Mentor can explain concepts, give hints and help you reflect — responsibly. Tap the button at the bottom-right.</p>
                        </Card>
                    </div>
                </div>
            )}

            {tab === "learning" && (
                <div className="space-y-4">
                    {enr ? (
                        <Card className="p-6">
                            <div className="flex items-center justify-between flex-wrap gap-3">
                                <div><Badge className="rounded-full">{enr.status}</Badge><h3 className="mt-3 font-heading text-xl font-bold">{enr.program_title}</h3><p className="text-sm text-foreground/50 mt-1">Current lesson: {enr.current_lesson}</p></div>
                                <div className="text-right"><p className="font-heading text-2xl font-extrabold text-primary">{enr.progress}%</p><p className="text-xs text-foreground/50">complete</p></div>
                            </div>
                            <Progress value={enr.progress} className="h-2 mt-4" />
                            <div className="mt-4 rounded-xl bg-muted/50 p-4 text-sm flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Next: {enr.next_action}</div>
                            <Button className="mt-4 rounded-full" data-testid="open-course-btn" onClick={() => navigate(`/learn/${enr.program_slug}`)}>Open course player <ArrowRight className="ml-1 h-4 w-4" /></Button>
                        </Card>
                    ) : <EmptyState icon={BookOpen} title="No program yet" desc="Enroll in a program to start learning." />}
                </div>
            )}

            {tab === "projects" && (
                <div className="space-y-4">
                    {projects.length ? projects.map((p) => (
                        <Card key={p.id} className="p-5" data-testid={`project-${p.id}`}>
                            <div className="flex items-start justify-between flex-wrap gap-3">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2"><h3 className="font-heading font-bold">{p.title}</h3><Badge variant="outline" className="rounded-full text-xs">AI: {p.ai_policy}</Badge></div>
                                    <p className="mt-1 text-sm text-foreground/60">{p.scenario}</p>
                                    {p.feedback && <p className="mt-2 text-sm text-foreground/70 rounded-lg bg-muted/50 p-3"><span className="font-medium">Mentor feedback:</span> {p.feedback}</p>}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <Badge className={`rounded-full bg-${STATUS_COLOR[p.status] || "muted"}/15 text-${STATUS_COLOR[p.status] || "foreground"} border-0`}>{p.status}</Badge>
                                    {["Assigned", "Started", "Revision"].includes(p.status) && <Button size="sm" className="rounded-full" data-testid={`explain-submit-${p.id}`} onClick={() => { setEx({ what: "", why: "", tools: "", decisions: "", problems: "", ai_tools: "", learned: "", video_url: "" }); setExplainFor(p); }}>Explain &amp; Submit</Button>}
                                </div>
                            </div>
                        </Card>
                    )) : <EmptyState icon={FolderKanban} title="No projects assigned yet" desc="Projects will appear here once your program assigns them." />}
                </div>
            )}

            {tab === "assessments" && (
                <div className="space-y-4">
                    {assessments.length ? assessments.map((a) => (
                        <Card key={a.id} className="p-5 flex items-center justify-between flex-wrap gap-3" data-testid={`assessment-${a.id}`}>
                            <div>
                                <div className="flex items-center gap-2"><h3 className="font-heading font-bold">{a.title}</h3><Badge variant="outline" className="rounded-full text-xs">AI: {a.ai_mode}</Badge></div>
                                <p className="mt-1 text-sm text-foreground/60">{a.description || `${a.type} · ${a.question_count} questions · pass ${a.pass_score}%`}</p>
                            </div>
                            <Button className="rounded-full" data-testid={`take-assessment-${a.id}`} onClick={() => navigate(`/assessments/${a.id}`)}>Take assessment <ArrowRight className="ml-1 h-4 w-4" /></Button>
                        </Card>
                    )) : <EmptyState icon={ClipboardList} title="No assessments available" desc="Assessments for your programs will appear here." />}
                </div>
            )}

            {tab === "passport" && (
                data.passport ? (
                    <Card className="p-6 max-w-2xl">
                        <div className="flex items-center justify-between border-b border-border pb-4">
                            <span className="flex items-center gap-2 font-heading font-bold"><IdCard className="h-5 w-5 text-primary" /> Digital Passport</span>
                            <Badge className="bg-success/15 text-success border-0">Active</Badge>
                        </div>
                        <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                            <div><p className="text-foreground/50">Passport ID</p><p className="font-mono text-xs mt-0.5 break-all">{data.passport.passport_id}</p></div>
                            <div><p className="text-foreground/50">Experience Level</p><p className="font-medium mt-0.5">{data.passport.level}</p></div>
                            <div><p className="text-foreground/50">LPRS</p><p className="font-medium mt-0.5">{data.passport.lprs} / 100</p></div>
                        </div>
                        <p className="mt-4 text-foreground/50 text-sm">Verified skills</p>
                        <div className="mt-2 flex flex-wrap gap-2">{(data.passport.verified_skills || []).map((s) => <Badge key={s} variant="secondary" className="rounded-full">{s}</Badge>)}</div>
                    </Card>
                ) : <EmptyState icon={IdCard} title="No passport yet" desc="Complete activities to build your Digital Passport." />
            )}

            {tab === "credentials" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {certs.length ? certs.map((c) => (
                        <Card key={c.id} className="p-5" data-testid={`cert-${c.credential_id}`}>
                            <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-xl bg-success/15 text-success"><Award className="h-5 w-5" /></span><div><h3 className="font-heading font-bold">{c.program}</h3><p className="text-xs text-foreground/50">{c.credential_type} · Level {c.level}</p></div></div>
                            <div className="mt-4 flex items-center justify-between text-sm"><span className="font-mono text-xs text-foreground/60">{c.credential_id}</span><Badge className="bg-success/15 text-success border-0">{c.status}</Badge></div>
                        </Card>
                    )) : <EmptyState icon={Award} title="No certificates issued yet" desc="Earn credentials by demonstrating competency." />}
                </div>
            )}

            <Dialog open={!!explainFor} onOpenChange={(o) => !o && setExplainFor(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="explain-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Explain Your Work</DialogTitle>
                        <DialogDescription>Tell your mentor how you built this — it verifies the work is genuinely yours.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div><Label>What did you create?</Label><Textarea data-testid="explain-what" className="mt-1" rows={2} value={ex.what} onChange={setField("what")} /></div>
                        <div><Label>Why did you make these choices?</Label><Textarea className="mt-1" rows={2} value={ex.why} onChange={setField("why")} /></div>
                        <div><Label>Which tools did you use?</Label><Input className="mt-1" value={ex.tools} onChange={setField("tools")} /></div>
                        <div><Label>What problems did you hit &amp; how did you solve them?</Label><Textarea className="mt-1" rows={2} value={ex.problems} onChange={setField("problems")} /></div>
                        <div><Label>Which AI tools did you use, and how?</Label><Textarea data-testid="explain-ai" className="mt-1" rows={2} value={ex.ai_tools} onChange={setField("ai_tools")} /></div>
                        <div><Label>What did you learn?</Label><Textarea className="mt-1" rows={2} value={ex.learned} onChange={setField("learned")} /></div>
                        <div><Label>Video/audio explanation URL (optional)</Label><Input className="mt-1" placeholder="https://…" value={ex.video_url} onChange={setField("video_url")} /></div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setExplainFor(null)}>Cancel</Button>
                        <Button onClick={submitExplain} data-testid="explain-submit-confirm-btn">Submit for review</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
